import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePlan } from "../context/PlanContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PromoBanner from "../components/PromoBanner";
import { Bell, BellOff, Phone, Plus, Trash2, MessageSquare, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const ALERT_TYPES = [
  { id: "price_above", label: "Price above" },
  { id: "price_below", label: "Price below" },
  { id: "rsi_above", label: "RSI above" },
  { id: "rsi_below", label: "RSI below" },
  { id: "macd_bullish", label: "MACD bullish" },
  { id: "macd_bearish", label: "MACD bearish" },
  { id: "vwap_reclaim", label: "VWAP reclaim" },
  { id: "vwap_reject", label: "VWAP reject" },
  { id: "breakout", label: "Breakout" },
  { id: "breakdown", label: "Breakdown" },
  { id: "volume_spike", label: "Volume spike (RVOL)" },
  { id: "setup_score", label: "Setup score reached" },
];

function PhoneVerifier({ onDone }) {
  const [phone, setPhone] = useState("+1");
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState("");
  const [debugCode, setDebugCode] = useState(null);
  const [stubbed, setStubbed] = useState(false);

  const start = async () => {
    try {
      const { data } = await api.post("/api/alerts/phone/start", { phone_number: phone });
      setOtpSent(true);
      setStubbed(!!data.stubbed);
      setDebugCode(data.debug_code || null);
      toast.success(data.stubbed ? "OTP sent (Twilio stubbed — code shown below)" : "OTP sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send OTP");
    }
  };
  const confirm = async () => {
    try {
      await api.post("/api/alerts/phone/confirm", { code });
      toast.success("Phone verified");
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed");
    }
  };

  return (
    <div className="card-surface p-4" data-testid="phone-verifier">
      <div className="flex items-center gap-2 mb-2">
        <Phone className="w-3.5 h-3.5 text-white/60" />
        <span className="text-white text-[14px] font-semibold">Verify phone for SMS alerts</span>
      </div>
      {!otpSent ? (
        <div className="flex gap-2">
          <input value={phone} onChange={(e) => setPhone(e.target.value)}
                 placeholder="+14155551234" className="input-glass flex-1" data-testid="phone-input"/>
          <button onClick={start} className="btn-accent" data-testid="phone-send-otp">Send code</button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value)}
                 placeholder="123456" className="input-glass flex-1" data-testid="otp-input"/>
          <button onClick={confirm} className="btn-accent" data-testid="phone-confirm">Confirm</button>
        </div>
      )}
      {stubbed && debugCode && (
        <div className="mt-2 text-[11px] text-white/60">
          Twilio not configured. Debug code (DEV only): <span className="font-mono text-[var(--bull)]">{debugCode}</span>
        </div>
      )}
    </div>
  );
}

function AlertRow({ a, onToggle, onDelete }) {
  const chan = a.channel;
  const badge =
    chan === "sms" ? "SMS" : chan === "both" ? "SMS+App" : "In-App";
  return (
    <tr className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`alert-row-${a.id}`}>
      <td className="px-3 py-2.5 text-white font-semibold">{a.ticker}</td>
      <td className="px-3 py-2.5 text-white/85">{ALERT_TYPES.find((t) => t.id === a.type)?.label || a.type}</td>
      <td className="px-3 py-2.5 font-mono tabular-nums text-white/85">{a.threshold}</td>
      <td className="px-3 py-2.5">
        <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-white/[0.05] border border-white/10 text-white/75">
          {badge}
        </span>
      </td>
      <td className="px-3 py-2.5 text-white/60 text-[11px] font-mono">
        {a.last_triggered_at ? new Date(a.last_triggered_at).toLocaleString() : "—"}
      </td>
      <td className="px-3 py-2.5 text-right">
        <button onClick={() => onToggle(a)} className="text-white/60 hover:text-white p-1" title={a.active ? "Pause" : "Resume"}
                data-testid={`alert-toggle-${a.id}`}>
          {a.active ? <Bell className="w-4 h-4 text-[var(--bull)]" /> : <BellOff className="w-4 h-4 text-white/40" />}
        </button>
        <button onClick={() => onDelete(a)} className="text-white/40 hover:text-[var(--bear)] p-1"
                data-testid={`alert-delete-${a.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

export default function Alerts() {
  const { limits, plan, showUpgrade } = usePlan();
  const [alerts, setAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [phoneStatus, setPhoneStatus] = useState({ verified: false, phone_number: null, twilio_configured: false });
  const [form, setForm] = useState({ ticker: "AAPL", type: "price_above", threshold: 200, channel: "in_app" });

  const load = async () => {
    try {
      const [a, h, p] = await Promise.all([
        api.get("/api/alerts"),
        api.get("/api/alerts/history"),
        api.get("/api/alerts/phone"),
      ]);
      setAlerts(a.data || []);
      setHistory(h.data || []);
      setPhoneStatus(p.data || {});
    } catch (e) {
      console.warn("[Alerts] load failed:", e);
    }
  };
  useEffect(() => { load(); }, []);

  const canSMS = !!limits.sms_alerts;
  const smsPhoneReady = canSMS && phoneStatus.verified;

  const create = async () => {
    if (!limits.in_app_alerts && !canSMS) { showUpgrade("alerts"); return; }
    if ((form.channel === "sms" || form.channel === "both")) {
      if (!canSMS) { showUpgrade("sms_alerts"); return; }
      if (!phoneStatus.verified) { toast.error("Verify your phone first"); return; }
    }
    try {
      await api.post("/api/alerts", { ...form, threshold: Number(form.threshold) });
      toast.success("Alert created");
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 402) {
        showUpgrade((d && d.code) || "alerts");
      } else {
        toast.error(formatApiErrorDetail(d) || "Failed to create alert");
      }
    }
  };

  const toggle = async (a) => {
    try {
      await api.patch(`/api/alerts/${a.id}`, { active: !a.active });
      await load();
    } catch (err) { toast.error("Update failed"); }
  };
  const del = async (a) => {
    if (!confirm(`Delete alert on ${a.ticker}?`)) return;
    try { await api.delete(`/api/alerts/${a.id}`); await load(); }
    catch (err) { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="alerts-page">
      <Header />
      <PromoBanner />
      <main id="main-content" className="flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="label-mono">Alerts</div>
          <h1 className="text-3xl sm:text-4xl text-white font-semibold tracking-tight mt-1">SMS & In-App Alerts</h1>
          <div className="text-white/50 text-[12px] mt-1">
            Plan: <span className="text-white/85 uppercase font-mono">{plan}</span> ·
            {" "}{limits.alerts == null ? "Unlimited" : limits.alerts} active alerts ·
            {" "}{canSMS ? "SMS ✓" : "In-app only (upgrade for SMS)"}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-4">
          <aside className="space-y-4">
            {/* Phone verification */}
            {canSMS && !phoneStatus.verified && <PhoneVerifier onDone={load} />}
            {canSMS && phoneStatus.verified && (
              <div className="card-surface p-4" data-testid="phone-verified">
                <div className="flex items-center gap-2 text-[13px]">
                  <Phone className="w-3.5 h-3.5 text-[var(--bull)]" />
                  <span className="text-white/85">Verified: <span className="font-mono">{phoneStatus.phone_number}</span></span>
                </div>
                {!phoneStatus.twilio_configured && (
                  <div className="mt-2 flex items-start gap-2 text-[11px] text-white/55">
                    <ShieldAlert className="w-3 h-3 text-[#ffd166] mt-0.5" />
                    Twilio credentials not configured — SMS delivery is stubbed. Add TWILIO_ACCOUNT_SID / AUTH_TOKEN / FROM_NUMBER to backend/.env.
                  </div>
                )}
              </div>
            )}

            {/* New alert form */}
            <div className="card-surface p-4" data-testid="new-alert-form">
              <div className="flex items-center gap-2 mb-3">
                <Plus className="w-3.5 h-3.5 text-white/60" />
                <span className="text-white text-[14px] font-semibold">New alert</span>
              </div>
              <div className="space-y-2.5">
                <div>
                  <label className="label-mono">Ticker</label>
                  <input value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value.toUpperCase() })}
                         className="input-glass w-full mt-1" data-testid="alert-ticker" />
                </div>
                <div>
                  <label className="label-mono">Type</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                          className="input-glass w-full mt-1" data-testid="alert-type">
                    {ALERT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono">Threshold</label>
                  <input type="number" step="any" value={form.threshold}
                         onChange={(e) => setForm({ ...form, threshold: e.target.value })}
                         className="input-glass w-full mt-1" data-testid="alert-threshold" />
                </div>
                <div>
                  <label className="label-mono">Channel</label>
                  <div className="grid grid-cols-3 gap-1.5 mt-1">
                    {["in_app", "sms", "both"].map((c) => (
                      <button key={c} onClick={() => setForm({ ...form, channel: c })}
                              disabled={c !== "in_app" && !smsPhoneReady}
                              className={`px-2 py-1.5 rounded-lg text-[11px] font-mono uppercase tracking-wider border transition ${
                                form.channel === c
                                  ? "bg-white/[0.08] border-white/40 text-white"
                                  : "bg-white/[0.02] border-white/10 text-white/60 hover:text-white"
                              } ${c !== "in_app" && !smsPhoneReady ? "opacity-40 cursor-not-allowed" : ""}`}
                              data-testid={`channel-${c}`}>
                        {c === "in_app" ? "In-App" : c === "sms" ? "SMS" : "Both"}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={create} className="btn-accent w-full mt-2" data-testid="alert-create-btn">
                  Create alert
                </button>
              </div>
            </div>
          </aside>

          <section className="space-y-4">
            <div className="card-surface p-0 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <Bell className="w-3.5 h-3.5 text-white/60" />
                  <span className="text-white text-[14px] font-semibold">Active alerts</span>
                </div>
                <div className="label-mono">{alerts.length}</div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white/[0.02] text-[10px] font-mono uppercase tracking-wider text-white/55">
                    <th className="text-left px-3 py-2.5">Ticker</th>
                    <th className="text-left px-3 py-2.5">Type</th>
                    <th className="text-left px-3 py-2.5">Threshold</th>
                    <th className="text-left px-3 py-2.5">Channel</th>
                    <th className="text-left px-3 py-2.5">Last triggered</th>
                    <th className="text-right px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-white/40 py-8 text-xs">No alerts yet</td></tr>
                  )}
                  {alerts.map((a) => <AlertRow key={a.id} a={a} onToggle={toggle} onDelete={del} />)}
                </tbody>
              </table>
            </div>

            <div className="card-surface p-0 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5 text-white/60" />
                  <span className="text-white text-[14px] font-semibold">Recent triggers</span>
                </div>
                <div className="label-mono">{history.length}</div>
              </div>
              <div className="divide-y divide-white/5">
                {history.length === 0 && (
                  <div className="text-center text-white/40 py-8 text-xs">No triggers yet</div>
                )}
                {history.slice(0, 20).map((h) => (
                  <div key={h.id} className="px-4 py-2.5 flex items-center justify-between" data-testid={`history-row-${h.id}`}>
                    <div className="text-[13px] text-white/85">{h.message}</div>
                    <div className="text-[11px] font-mono text-white/45">
                      {new Date(h.triggered_at).toLocaleString()}
                      {h.delivery?.sms?.stubbed && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-[#ffd166]/15 text-[#ffd166]">STUBBED</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
